import React from 'react'
import { useMutation } from '@apollo/client'
import {
  CreateNewGroup,
  CreateNewGroupVariables,
  FieldName,
  useGroupForm,
  useAuth,
  CompanyMemberRole,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Button from 'components/Button'
import Card from 'components/Card'
import { SelectInput } from 'components/SelectInput'
import { Dialog } from 'components/Dialog'
import TextInput from 'components/TextInput'
import { CREATE_NEW_GROUP } from './operations'

interface Props {
  companyId: string
  isOpen: boolean
  onDismiss: () => void
  onGroupCreated: (groupId: string) => void
  queriesToRefetch?: string[]
  isClient?: boolean
}

const CreateNewGroupDrawer: React.VFC<Props> = ({
  companyId,
  isOpen,
  onDismiss,
  onGroupCreated,
  queriesToRefetch,
  isClient,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const [createNewGroup, { loading: isCreateNewGroupLoading }] = useMutation<
    CreateNewGroup,
    CreateNewGroupVariables
  >(CREATE_NEW_GROUP, {
    ...(queriesToRefetch ? { awaitRefetchQueries: true, refetchQueries: queriesToRefetch } : {}),
  })
  const { getUserCompaniesWithRole } = useAuth()
  const { companies } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Manager, CompanyMemberRole.Owner],
  })
  const [company, setCompany] = React.useState(companies?.[0]?.id || companyId)

  const { groupForm } = useGroupForm({
    validationSchema: 'fullGroup',
    onSubmit: async (values, formikHelpers) => {
      try {
        const { data } = await createNewGroup({
          variables: {
            inputData: {
              name: values[FieldName.NAME],
              companyId: isClient ? company : companyId,
            },
          },
        })
        if (data?.createGroup?.id) {
          formikHelpers.resetForm()
          onGroupCreated(data.createGroup.id)
        }
      } catch {
        // ignore
      }
    },
  })

  return (
    <Dialog
      variant={isDesktop ? 'center' : 'fullscreen'}
      maxWidth={isDesktop ? '550px' : '100%'}
      isOpen={isOpen}
      onDismiss={onDismiss}
    >
      <Card>
        <Card.Title onDismiss={onDismiss}>New group</Card.Title>
        <Card.Content>
          <TextInput
            isFullWidth
            name="name"
            label="Group name"
            value={groupForm.values[FieldName.NAME]}
            onChange={groupForm.handleChange}
            onBlur={groupForm.handleBlur}
            errorMessage={
              groupForm.touched[FieldName.NAME] && groupForm.errors[FieldName.NAME]
                ? groupForm.errors[FieldName.NAME]
                : undefined
            }
          />
          {queriesToRefetch && (
            <SelectInput
              name="company"
              label="Company"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              options={companies.map((e) => ({ value: e.id, label: e.name }))}
              hasBottomContent={false}
            />
          )}
        </Card.Content>
        <Card.Actions>
          <Button
            onClick={() => {
              groupForm.handleSubmit()
            }}
            isFetching={isCreateNewGroupLoading}
          >
            Save
          </Button>
        </Card.Actions>
      </Card>
    </Dialog>
  )
}

export { CreateNewGroupDrawer }
